export default {

  /*
  |--------------------------------------------------------------------------
  | Section 1
  |--------------------------------------------------------------------------
  |
  | Identify whether the user should seek urgent medical advice.
  |
  */
  section1: {
    question1: {
      conditions: [
        // Neck
        {
          id: "k1XKgkvx",

          title: [
            "It's not unusual for neck problems to also give you symptoms in the head, shoulder region, arms and even your hands.",
            "However, should you be experiencing any of the following symptoms since you noticed your neck problem, these could be a sign of something more serious and you should seek urgent medical advice:",
          ],

          details: [
            "Chest pains",
            "Pain when breathing or shortness of breath",
            "Significant swelling to your neck or shoulder, especially if it is bright red and hot to touch",
            "Nausea or sickness",
            "Blurred or double vision",
            "Problems with your speech",
            "Fainting or blackouts",
            "Problems walking, such as clumsiness, unsteadiness or a lack of coordination",
            "Coughing or problems swallowing when you're drinking",
            "Numb face or tongue",
            "Weakness in both legs",
          ],

          footNote: "Having considered the above, to what extent do you feel you might be experiencing these symptoms?",

          only: [
            "neck",
          ],

          answers: {
            "Not sure": "not sure",
            "Some symptoms": "some symptoms",
            "No symptoms": "no symptoms",
          },

          needsAppointment(answer) {
            return answer === 'not sure';
          },

          confirmation(answer) {
            switch(answer) {
              case 'not sure':
                  return 'If you\'re not sure whether you have these symptoms, there will be the opportunity to discuss this with your physio if an appointment is booked with us';
              case 'some symptoms':
                return 'I do have one or more of these symptoms but have already seen or spoken with an appropriate healthcare professional who was satisfied there were no urgent or immediate concerns.'; 
            }

            return false;
          }
        },

        // Upper Back
        {
          id: "5AhdywNI",

          title: [
            "It's not unusual for upper back problems to also give you symptoms in the ribs or chest, especially when coughing, twisting or taking a deep breath.",
            "However, should you be experiencing any of the following symptoms since you noticed your upper back symptoms, these could be a sign of something more serious and you should seek urgent medical advice:",
          ],

          details: [
            "Abdominal pain",
            "Shortness of breath",
            "Pain in your upper back that either doesn't ease or gets worse when you lie down",
          ],

          footNote: "Having considered the above, to what extent do you feel you might be experiencing these symptoms?",

          only: [
            "thoracic",
          ],

          answers: {
            "Not sure": "not sure",
            "Some symptoms": "some symptoms",
            "No symptoms": "no symptoms",
          },

          needsAppointment(answer) {
            return answer === 'not sure';
          },

          confirmation(answer) {
            switch(answer) {
              case 'not sure':
                  return 'If you\'re not sure whether you have these symptoms, there will be the opportunity to discuss this with your physio if an appointment is booked with us';
              case 'some symptoms':
                return 'I do have one or more of these symptoms but have already seen or spoken with an appropriate healthcare professional who was satisfied there were no urgent or immediate concerns.'; 
            }

            return false;
          }
        },

        // Lower Back
        {
          id: "TjiCbPu6",

          title: [
            "It's not unusual for lower back problems to also give you symptoms in the buttocks, groin, front of your thigh and sometimes further down your legs.",
            "However, should you be experiencing a recent change in any of the following symptoms, these could be a sign of something more serious and you should seek urgent medical advice:",
          ],

          details: [
            "Not being able to tell when you need to go to the loo",
            "A feeling of not completely emptying your bladder",
            "Straining to pass urine",
            "Leaking of some urine when you've laughed, coughed or jumped",
            "Not being able to feel toilet paper around your bottom",
            "Incontinence",
            "Losing the ability to have sex",
            "Numbness or tingling in both legs",
            "Shooting pains down the length of both your legs",
            "Weakness in both legs",
            "Problems walking, such as clumsiness, unsteadiness or a lack of coordination",
          ],

          footNote: "Having considered the above, to what extent do you feel you might be experiencing these symptoms?",

          only: [
            "lumbar",
          ],

          answers: {
            "Not sure": "not sure",
            "Some symptoms": "some symptoms",
            "No symptoms": "no symptoms",
          },

          needsAppointment(answer) {
            return answer === 'not sure';
          },

          confirmation(answer) {
            switch(answer) {
              case 'not sure':
                  return 'If you\'re not sure whether you have these symptoms, there will be the opportunity to discuss this with your physio if an appointment is booked with us';
              case 'some symptoms':
                return 'I do have one or more of these symptoms but have already seen or spoken with an appropriate healthcare professional who was satisfied there were no urgent or immediate concerns.'; 
            }

            return false;
          }
        },

        // Shoulder (Front & Back)
        {
          id: "bLGcmq5c",

          title: [
            "It's not unusual for shoulder problems to also give you symptoms in the neck, upper arm, forearm, hand and wrist.",
            "However, should you be feeling any of the following symptoms, these could be a sign of something more serious and you should seek urgent medical advice:",
          ],

          details: [
            "Chest pains",
            "Pain when breathing or shortness of breath",
            "Significant swelling to your shoulder joint or if it is bright red and hot to touch",
            "Flu-like symptoms that don't seem to be getting better as soon as they should",
          ],

          footNote: "Having considered the above, to what extent do you feel you might be experiencing these symptoms?",

          only: [
            "shoulderFrontLeft",
            "shoulderFrontRight",
            "shoulderBackLeft",
            "shoulderBackRight",
          ],

          answers: {
            "Not sure": "not sure",
            "Some symptoms": "some symptoms",
            "No symptoms": "no symptoms",
          },

          needsAppointment(answer) {
            return answer === 'not sure';
          },

          confirmation(answer) {
            switch(answer) {
              case 'not sure':
                  return 'If you\'re not sure whether you have these symptoms, there will be the opportunity to discuss this with your physio if an appointment is booked with us';
              case 'some symptoms':
                return 'I do have one or more of these symptoms but have already seen or spoken with an appropriate healthcare professional who was satisfied there were no urgent or immediate concerns.'; 
            }

            return false;
          }
        },

        // Upper Arm
        {
          id: "x88ziTPd",

          title: [
            "It's not unusual for pain in the upper arm to be linked with problems of the neck or shoulder, as well as muscles of the upper arm.",
            "However, should you be feeling any of the following symptoms, these could be a sign of something more serious and you should seek urgent medical advice:",
          ],

          details: [
            "Chest pains",
            "Pain when breathing or shortness of breath",
            "Significant swelling to your shoulder joint or if it is bright red and hot to touch",
            "Flu-like symptoms that don't seem to be getting better as soon as they should",
          ],

          footNote: "Having considered the above, to what extent do you feel you might be experiencing these symptoms?",

          only: [
            "upperArmLeft",
            "upperArmRight",
          ],

          answers: {
            "Not sure": "not sure",
            "Some symptoms": "some symptoms",
            "No symptoms": "no symptoms",
          },

          needsAppointment(answer) {
            return answer === 'not sure';
          },

          confirmation(answer) {
            switch(answer) {
              case 'not sure':
                  return 'If you\'re not sure whether you have these symptoms, there will be the opportunity to discuss this with your physio if an appointment is booked with us';
              case 'some symptoms':
                return 'I do have one or more of these symptoms but have already seen or spoken with an appropriate healthcare professional who was satisfied there were no urgent or immediate concerns.'; 
            }

            return false;
          }
        },

        // Forearm
        {
          id: "SiIACz8z",

          title: [
            "It's not unusual for pain in the forearm to be linked with problems of the neck, shoulder, elbow or wrist, as well as muscles of the forearm.",
            "However, should you be feeling any of the following symptoms, these could be a sign of something more serious and you should seek urgent medical advice:",
          ],

          details: [
            "Chest pains",
            "Pain when breathing or shortness of breath",
            "Significant swelling to your shoulder, elbow or wrist joints or if they are bright red and hot to touch",
            "Flu-like symptoms that don't seem to be getting better as soon as they should",
          ],

          footNote: "Having considered the above, to what extent do you feel you might be experiencing these symptoms?",

          only: [
            "forearmLeft",
            "forearmRight",
          ],

          answers: {
            "Not sure": "not sure",
            "Some symptoms": "some symptoms",
            "No symptoms": "no symptoms",
          },

          needsAppointment(answer) {
            return answer === 'not sure';
          },

          confirmation(answer) {
            switch(answer) {
              case 'not sure':
                  return 'If you\'re not sure whether you have these symptoms, there will be the opportunity to discuss this with your physio if an appointment is booked with us';
              case 'some symptoms':
                return 'I do have one or more of these symptoms but have already seen or spoken with an appropriate healthcare professional who was satisfied there were no urgent or immediate concerns.'; 
            }

            return false;
          }
        },

        // Elbow
        {
          id: "6dFES0gw",

          title: [
            "It's not unusual for elbow problems to also give you symptoms in the forearm, hand and wrist.",
            "However, should you be feeling any of the following symptoms, these could be a sign of something more serious and you should seek urgent medical advice:",
          ],

          details: [
            "Chest pains",
            "Pain when breathing or shortness of breath",
            "Significant swelling to your elbow joint or if it is bright red and hot to touch",
            "Flu-like symptoms that don't seem to be getting better as soon as they should",
          ],

          footNote: "Having considered the above, to what extent do you feel you might be experiencing these symptoms?",

          only: [
            "elbowLeft",
            "elbowRight",
          ],

          answers: {
            "Not sure": "not sure",
            "Some symptoms": "some symptoms",
            "No symptoms": "no symptoms",
          },

          needsAppointment(answer) {
            return answer === 'not sure';
          },

          confirmation(answer) {
            switch(answer) {
              case 'not sure':
                  return 'If you\'re not sure whether you have these symptoms, there will be the opportunity to discuss this with your physio if an appointment is booked with us';
              case 'some symptoms':
                return 'I do have one or more of these symptoms but have already seen or spoken with an appropriate healthcare professional who was satisfied there were no urgent or immediate concerns.'; 
            }

            return false;
          }
        },

        // Wrist & Hand
        {
          id: "8DKMoC0I",

          title: [
            "It's not unusual for wrist and hand problems to also give you symptoms in the fingers.",
            "However, should you be feeling any of the following symptoms, these could be a sign of something more serious and you should seek urgent medical advice:",
          ],

          details: [
            "Significant swelling to your wrist or hand joints or if they are bright red and hot to touch",
            "Flu-like symptoms that don't seem to be getting better as soon as they should",
          ],

          footNote: "Having considered the above, to what extent do you feel you might be experiencing these symptoms?",

          only: [
            "handAndWristLeft",
            "handAndWristRight",
          ],

          answers: {
            "Not sure": "not sure",
            "Some symptoms": "some symptoms",
            "No symptoms": "no symptoms",
          },

          needsAppointment(answer) {
            return answer === 'not sure';
          },

          confirmation(answer) {
            switch(answer) {
              case 'not sure':
                  return 'If you\'re not sure whether you have these symptoms, there will be the opportunity to discuss this with your physio if an appointment is booked with us';
              case 'some symptoms':
                return 'I do have one or more of these symptoms but have already seen or spoken with an appropriate healthcare professional who was satisfied there were no urgent or immediate concerns.'; 
            }

            return false;
          }
        },

        // Hip / Pelvis
        {
          id: "VfL1fvBp",

          title: [
            "It's not unusual for hip/pelvic problems to also give you symptoms in the groin, thigh, buttocks and down towards your knee.",
            "However, should you be experiencing a recent change in any of the following symptoms, these could be a sign of something more serious and you should seek urgent medical advice:",
          ],

          details: [
            "Being unable to bear weight or take more than 3 steps on your injured leg",
            "Flu-like symptoms that don't seem to be getting better as soon as they should",
            "Not being able to tell when you need to go to the loo",
            "A feeling of not completely emptying your bladder",
            "Straining to pass urine",
            "Leaking of some urine when you've laughed, coughed or jumped",
            "Not being able to feel toilet paper around your bottom",
            "Incontinence",
            "Losing the ability to have sex",
            "Numbness or tingling in both legs or feet",
            "Shooting pains down the length of both your legs",
            "Weakness in both legs",
            "Problems walking, such as clumsiness, unsteadiness or a lack of coordination",
          ],

          footNote: "Having considered the above, to what extent do you feel you might be experiencing these symptoms?",

          only: [
            "hipLeft",
            "hipRight",
          ],

          answers: {
            "Not sure": "not sure",
            "Some symptoms": "some symptoms",
            "No symptoms": "no symptoms",
          },

          needsAppointment(answer) {
            return answer === 'not sure';
          },

          confirmation(answer) {
            switch(answer) {
              case 'not sure':
                  return 'If you\'re not sure whether you have these symptoms, there will be the opportunity to discuss this with your physio if an appointment is booked with us';
              case 'some symptoms':
                return 'I do have one or more of these symptoms but have already seen or spoken with an appropriate healthcare professional who was satisfied there were no urgent or immediate concerns.'; 
            }

            return false;
          }
        },

        // Groin
        {
          id: "qqwjAad8",

          title: [
            "It's not unusual for groin problems to also give you symptoms in the hip, thigh, buttocks and down towards your knee.",
            "However, should you be experiencing a recent change in any of the following symptoms, these could be a sign of something more serious and you should seek urgent medical advice:",
          ],

          details: [
            "Being unable to bear weight or take more than 3 steps on your injured leg",
            "Flu-like symptoms that don't seem to be getting better as soon as they should",
            "Not being able to tell when you need to go to the loo",
            "A feeling of not completely emptying your bladder",
            "Straining to pass urine",
            "Leaking of some urine when you've laughed, coughed or jumped",
            "Not being able to feel toilet paper around your bottom",
            "Incontinence",
            "Losing the ability to have sex",
            "Numbness or tingling in both legs or feet",
            "Shooting pains down the length of both your legs",
            "Weakness in both legs",
            "Problems walking, such as clumsiness, unsteadiness or a lack of coordination",
            "Not being able to pass gas or open bowels",
            "Groin swelling which turns red, purple or dark in colour",
            "Nausea or vomiting",
            "Abdominal pain",
          ],

          footNote: "Having considered the above, to what extent do you feel you might be experiencing these symptoms?",

          only: [
            "groinLeft",
            "groinRight",
          ],

          answers: {
            "Not sure": "not sure",
            "Some symptoms": "some symptoms",
            "No symptoms": "no symptoms",
          },

          needsAppointment(answer) {
            return answer === 'not sure';
          },

          confirmation(answer) {
            switch(answer) {
              case 'not sure':
                  return 'If you\'re not sure whether you have these symptoms, there will be the opportunity to discuss this with your physio if an appointment is booked with us';
              case 'some symptoms':
                return 'I do have one or more of these symptoms but have already seen or spoken with an appropriate healthcare professional who was satisfied there were no urgent or immediate concerns.'; 
            }

            return false;
          }
        },

        // Thigh
        {
          id: "YeBGSzed",

          title: [
            "It's not unusual for thigh problems to be linked to problems with the lower back, hip or knee, as well as the muscles of the thigh.",
            "However, should you be experiencing a recent change in any of the following symptoms, these could be a sign of something more serious and you should seek urgent medical advice:",
          ],

          details: [
            "Being unable to bear weight or take more than 3 steps on your injured leg",
            "Flu-like symptoms that don't seem to be getting better as soon as they should",
            "Not being able to tell when you need to go to the loo",
            "A feeling of not completely emptying your bladder",
            "Straining to pass urine",
            "Leaking of some urine when you've laughed, coughed or jumped",
            "Not being able to feel toilet paper around your bottom",
            "Incontinence",
            "Losing the ability to have sex",
            "Numbness or tingling in both legs or feet",
            "Shooting pains down the length of both your legs",
            "Weakness in both legs",
            "Problems walking, such as clumsiness, unsteadiness or a lack of coordination",
          ],

          footNote: "Having considered the above, to what extent do you feel you might be experiencing these symptoms?",

          only: [
            "thighFrontLeft",
            "thighFrontRight",
            "thighBackLeft",
            "thighBackRight",
          ],

          answers: {
            "Not sure": "not sure",
            "Some symptoms": "some symptoms",
            "No symptoms": "no symptoms",
          },

          needsAppointment(answer) {
            return answer === 'not sure';
          },

          confirmation(answer) {
            switch(answer) {
              case 'not sure':
                  return 'If you\'re not sure whether you have these symptoms, there will be the opportunity to discuss this with your physio if an appointment is booked with us';
              case 'some symptoms':
                return 'I do have one or more of these symptoms but have already seen or spoken with an appropriate healthcare professional who was satisfied there were no urgent or immediate concerns.'; 
            }

            return false;
          }
        },

        // Knee (Front & Back)
        {
          id: "bdmHMpWg",

          title: [
            "It's not unusual for knee problems to also give you symptoms above or below your knee and can cause your knee to swell, click, lock, catch or occasionally feel like it is giving way.",
            "However, should you be feeling any of the following symptoms, these could be a sign of something more serious and you should seek urgent medical advice:",
          ],

          details: [
            "Being unable to bear weight or take more than 3 steps on your injured leg",
            "Significant swelling to your knee joint or calf or if it is bright red and hot to touch",
            "Flu-like symptoms that don't seem to be getting better as soon as they should",
            "Not being able to tell when you need to go to the loo",
            "A feeling of not completely emptying your bladder",
            "Straining to pass urine",
            "Leaking of some urine when you've laughed, coughed or jumped",
            "Not being able to feel toilet paper around your bottom",
            "Incontinence",
            "Losing the ability to have sex",
            "Numbness or tingling in both legs or feet",
            "Shooting pains down the length of both your legs",
            "Weakness in both legs",
            "Problems walking, such as clumsiness, unsteadiness or a lack of coordination",
          ],

          footNote: "Having considered the above, to what extent do you feel you might be experiencing these symptoms?",

          only: [
            "kneeLeft",
            "kneeRight",
            "posteriorKneeLeft",
            "posteriorKneeRight",
          ],

          answers: {
            "Not sure": "not sure",
            "Some symptoms": "some symptoms",
            "No symptoms": "no symptoms",
          },

          needsAppointment(answer) {
            return answer === 'not sure';
          },

          confirmation(answer) {
            switch(answer) {
              case 'not sure':
                  return 'If you\'re not sure whether you have these symptoms, there will be the opportunity to discuss this with your physio if an appointment is booked with us';
              case 'some symptoms':
                return 'I do have one or more of these symptoms but have already seen or spoken with an appropriate healthcare professional who was satisfied there were no urgent or immediate concerns.'; 
            }

            return false;
          }
        },

        // Shin
        {
          id: "oNIN4gIt",

          title: [
            "It's not unusual for shin problems to be linked to problems with the lower back, hip or knee, as well as the muscles of the shin area.",
            "However, should you be feeling any of the following symptoms, these could be a sign of something more serious and you should seek urgent medical advice:",
          ],

          details: [
            "Being unable to bear weight or take more than 3 steps on your injured leg",
            "Significant swelling to your knee joint or calf/shin or if it is bright red and hot to touch",
            "Flu-like symptoms that don't seem to be getting better as soon as they should",
            "Not being able to tell when you need to go to the loo",
            "A feeling of not completely emptying your bladder",
            "Straining to pass urine",
            "Leaking of some urine when you've laughed, coughed or jumped",
            "Not being able to feel toilet paper around your bottom",
            "Incontinence",
            "Losing the ability to have sex",
            "Numbness or tingling in both legs or feet",
            "Shooting pains down the length of both your legs",
            "Weakness in both legs",
            "Problems walking, such as clumsiness, unsteadiness or a lack of coordination",
          ],

          footNote: "Having considered the above, to what extent do you feel you might be experiencing these symptoms?",

          only: [
            "shinLeft",
            "shinRight",
          ],

          answers: {
            "Not sure": "not sure",
            "Some symptoms": "some symptoms",
            "No symptoms": "no symptoms",
          },

          needsAppointment(answer) {
            return answer === 'not sure';
          },

          confirmation(answer) {
            switch(answer) {
              case 'not sure':
                  return 'If you\'re not sure whether you have these symptoms, there will be the opportunity to discuss this with your physio if an appointment is booked with us';
              case 'some symptoms':
                return 'I do have one or more of these symptoms but have already seen or spoken with an appropriate healthcare professional who was satisfied there were no urgent or immediate concerns.'; 
            }

            return false;
          }
        },

        // Calf
        {
          id: "Vj7f0b3n",

          title: [
            "It's not unusual for calf problems to be linked to problems with the lower back or knee, as well as the muscles of the calf area.",
            "However, should you be feeling any of the following symptoms, these could be a sign of something more serious and you should seek urgent medical advice:",
          ],

          details: [
            "Being unable to bear weight or take more than 3 steps on your injured leg",
            "Significant swelling to your knee joint or calf or if it is bright red and hot to touch",
            "Flu-like symptoms that don't seem to be getting better as soon as they should",
            "Not being able to tell when you need to go to the loo",
            "A feeling of not completely emptying your bladder",
            "Straining to pass urine",
            "Leaking of some urine when you've laughed, coughed or jumped",
            "Not being able to feel toilet paper around your bottom",
            "Incontinence",
            "Losing the ability to have sex",
            "Numbness or tingling in both legs or feet",
            "Shooting pains down the length of both your legs",
            "Weakness in both legs",
            "Problems walking, such as clumsiness, unsteadiness or a lack of coordination",
          ],

          footNote: "Having considered the above, to what extent do you feel you might be experiencing these symptoms?",

          only: [
            "calfLeft",
            "calfRight",
          ],

          answers: {
            "Not sure": "not sure",
            "Some symptoms": "some symptoms",
            "No symptoms": "no symptoms",
          },

          needsAppointment(answer) {
            return answer === 'not sure';
          },

          confirmation(answer) {
            switch(answer) {
              case 'not sure':
                  return 'If you\'re not sure whether you have these symptoms, there will be the opportunity to discuss this with your physio if an appointment is booked with us';
              case 'some symptoms':
                return 'I do have one or more of these symptoms but have already seen or spoken with an appropriate healthcare professional who was satisfied there were no urgent or immediate concerns.'; 
            }

            return false;
          }
        },

        // Heel
        {
          id: "i10yVgpR",

          title: [
            "It's not unusual for heel problems to be linked to problems with the lower back or knee, as well as the muscles/tendons of the calf area.",
            "However, should you be feeling any of the following symptoms, these could be a sign of something more serious and you should seek urgent medical advice:",
          ],

          details: [
            "Being unable to bear weight or take more than 3 steps on your injured leg",
            "Significant swelling to your knee joint or calf or if it is bright red and hot to touch",
            "Flu-like symptoms that don't seem to be getting better as soon as they should",
            "Not being able to tell when you need to go to the loo",
            "A feeling of not completely emptying your bladder",
            "Straining to pass urine",
            "Leaking of some urine when you've laughed, coughed or jumped",
            "Not being able to feel toilet paper around your bottom",
            "Incontinence",
            "Losing the ability to have sex",
            "Numbness or tingling in both legs or feet",
            "Shooting pains down the length of both your legs",
            "Weakness in both legs",
            "Problems walking, such as clumsiness, unsteadiness or a lack of coordination",
          ],

          footNote: "Having considered the above, to what extent do you feel you might be experiencing these symptoms?",

          only: [
            "heelLeft",
            "heelRight",
          ],

          answers: {
            "Not sure": "not sure",
            "Some symptoms": "some symptoms",
            "No symptoms": "no symptoms",
          },

          needsAppointment(answer) {
            return answer === 'not sure';
          },

          confirmation(answer) {
            switch(answer) {
              case 'not sure':
                  return 'If you\'re not sure whether you have these symptoms, there will be the opportunity to discuss this with your physio if an appointment is booked with us';
              case 'some symptoms':
                return 'I do have one or more of these symptoms but have already seen or spoken with an appropriate healthcare professional who was satisfied there were no urgent or immediate concerns.'; 
            }

            return false;
          }
        },

        // Foot & Ankle
        {
          id: "Up8BmvCg",

          title: [
            "It's not unusual for foot/ankle problems to also give you symptoms into your shin, calf or toes and can cause your ankle to feel unstable.",
            "However, should you be feeling any of the following symptoms, these could be a sign of something more serious and you should seek urgent medical advice:",
          ],

          details: [
            "Being unable to bear weight or take more than 3 steps on your injured leg",
            "Significant swelling to your foot, calf or ankle joint or if it is bright red and hot to touch",
            "Flu-like symptoms that don't seem to be getting better as soon as they should",
            "Not being able to tell when you need to go to the loo",
            "A feeling of not completely emptying your bladder",
            "Straining to pass urine",
            "Leaking of some urine when you've laughed, coughed or jumped",
            "Not being able to feel toilet paper around your bottom",
            "Incontinence",
            "Losing the ability to have sex",
            "Numbness or tingling in both legs or feet",
            "Shooting pains down the length of both your legs",
            "Weakness in both legs",
            "Problems walking, such as clumsiness, unsteadiness or a lack of coordination",
          ],

          footNote: "Having considered the above, to what extent do you feel you might be experiencing these symptoms?",

          only: [
            "footAndAnkleLeft",
            "footAndAnkleRight",
          ],

          answers: {
            "Not sure": "not sure",
            "Some symptoms": "some symptoms",
            "No symptoms": "no symptoms",
          },

          needsAppointment(answer) {
            return answer === 'not sure';
          },

          confirmation(answer) {
            switch(answer) {
              case 'not sure':
                  return 'If you\'re not sure whether you have these symptoms, there will be the opportunity to discuss this with your physio if an appointment is booked with us';
              case 'some symptoms':
                return 'I do have one or more of these symptoms but have already seen or spoken with an appropriate healthcare professional who was satisfied there were no urgent or immediate concerns.'; 
            }

            return false;
          }
        },
      ],
    },
  },

  /*
  |--------------------------------------------------------------------------
  | Section 2
  |--------------------------------------------------------------------------
  |
  | Identify whether the user should seek non-urgent medical advice.
  |
  */
  section2: {
    question1: {
      id: "KIUibN1P",

      title: "Since your symptoms started, have you:",

      answers: "checkboxes",

      details: [
        "Been in constant pain, no matter what you do?",
        "Had chest, upper back or rib pain?",
        "Felt tired or exhausted?",
        "Had flu-like symptoms that don’t seem to be getting better as soon as they should?",
        "Had back pain that gets worse when you lie down?",
        "Had a constant cough or been short of breath?",
        "Had stiff joints for more than two hours in the morning?",
        "Lost more than 10lbs / 4.5kg of body weight in the last three months without knowing how?",
      ],

      goStraightToAppointment() {
        return false;
      },

      needsAppointment(answer) {
        return answer !== false;
      },
    },

    question2: {
      id: "qLTnGmPL",

      title: "Have you ever:",

      answers: "checkboxes",

      details: [
        "Had cancer?",
        "Had a severe eating disorder?",
        "Taken oral steroids long-term?",
        "Had rheumatoid arthritis or another auto-immune condition?",
        "Been a heavy smoker?",
        "Had an alcohol problem?",
        "Had unstable diabetes?",
        "Been under the care of a medical professional for a long-term medical condition?"
      ],

      goStraightToAppointment() {
        return false;
      },

      needsAppointment(answer) {
        return answer !== false;
      },
    },

    question3: {
      id: "HCsGhZZS",

      title: "Since your symptoms started, have you:",

      answers: "checkboxes",

      details: [
        "Felt pain spreading?",
        "Had pins and needles?",
        "Felt numbness in the area?",
        "Felt tingling in the area?",
        "Been feeling noticeably weaker in your arms or legs?",
        "Not been able to move a limb?",
      ],

      goStraightToAppointment() {
        return false;
      },

      needsAppointment(answer) {
        return answer !== false;
      },
    },

    question4: {
      conditions: [
        {
          id: "X660ScVB",

          title: "Since your joint symptoms started, have you:",

          answers: "checkboxes",

          details: [
            "Felt the joint lock, where you have had to force it to move with your hands?",
            "Noticed any unusual or new clicking or clunking?",
            "Felt that the joint is coming out of place or dislocating?",
            "Experienced any painless giving way of the joint?",
            "Noticed a change in the normal shape of the joint (apart from small swellings after an injury)?",
          ],

          goStraightToAppointment() {
            return false;
          },

          needsAppointment(answer) {
            return answer !== false;
          },

          only: [
            "elbowLeft",
            "elbowRight",
            "footAndAnkleLeft",
            "footAndAnkleRight",
            "handAndWristLeft",
            "handAndWristRight",
            "hipLeft",
            "hipRight",
            "kneeLeft",
            "kneeRight",
            "posteriorKneeLeft",
            "posteriorKneeRight",
            "pelvis",
            "shoulderFrontLeft",
            "shoulderFrontRight",
            "shoulderBackLeft",
            "shoulderBackRight",
          ],
        }
      ],
    },
  },

  /*
  |--------------------------------------------------------------------------
  | Section 3
  |--------------------------------------------------------------------------
  |
  | Identify whether the user needs a video appointment.
  |
  */
  section3: {
    question1: {
      id: "PekKMA3w",

      title: "Why have you accessed this service today (select all that apply)",

      answers: "checkboxes",

      details: [
        "Pain",
        "Weakness",
        "Stiffness/reduced range of movement",
        "Instability",
        "Injury prevention",
        "I'd like to return to a particular activity or hobby",
        "General advice",
        "Other"
      ],
    },

    question2: {
      id: "lI35pUCU",

      title: "Use this slider to rate your pain as it's been today",

      answers: "range",

      range: {
        min: 0,
        max: 10,
        step: 1,
        prefix: "No pain",
        suffix: "Worst imaginable",
      },

      needsAppointment(answer) {
        return answer >= 4;
      },

      goStraightToAppointment() {
        return false;
      },

      skipQuestion(survey) {
        const question1 = survey.answers.section3.question1;

        if (!Array.isArray(question1)) {
          return true;
        }

        if (question1.indexOf(0) === -1) {
          return true;
        }

        return false;
      }
    },

    question3: {
      id: "pazeOaAW",

      title: "Have you seen a doctor or had any tests for these symptoms?",

      needsAppointment(answer) {
        return answer === true;
      },

      goStraightToAppointment() {
        return false;
      },
    },

    question4: {
      id: "2HDdpzOr",

      title: "How long have you had these symptoms?",

      answers: {
        "Less than 6 weeks": false,
        "More than 6 weeks": true,
      },

      needsAppointment(answer) {
        return answer === true;
      },

      goStraightToAppointment() {
        return false;
      },
    },

    question5: {
      id: "O0RhJw6G",

      title: "Have your symptoms stopped you...",

      answers: "checkboxes",

      details: [
        "Dressing or washing yourself?",
        "Practising your hobbies?",
        "Working?",
        "Sleeping?",
      ],

      needsAppointment(answer) {
        return answer === true;
      },

      goStraightToAppointment() {
        return false;
      },
    },

    question6: {
      id: "54oxdCRe",

      title: "Since they started, are your symptoms now...",

      answers: {
        "Worse": "worse",
        "Same": "same",
        "Improving": "improving",
      },

      needsAppointment(answer) {
        return answer === "worse";
      },

      goStraightToAppointment() {
        return false;
      },
    },

    question7: {
      id: "0eNXs7Mc",

      title: "How worried are you about your symptoms?",

      answers: "range",

      range: {
        min: 0,
        max: 10,
        step: 1,
        prefix: "Not worried",
        suffix: "Deeply concerned",
      },

      needsAppointment(answer) {
        return answer >= 4;
      },

      goStraightToAppointment() {
        return false;
      },
    },
  },
}
